import { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { StatusPill } from "@/components/ui/status-pill";
import { formatPrice } from "@/lib/pricing";
import ContactRevealCard from "./ContactRevealCard";
import ScoreBreakdown from "./ScoreBreakdown";
import CompletionDialog from "./CompletionDialog";
import ClaimDialog from "./ClaimDialog";
import { useTranslation } from "react-i18next";

interface OpportunityClaim {
  id: string;
  opportunity_id: string;
  status: string;
  reason_code: string;
  reason_text: string | null;
  operator_note: string | null;
  created_at: string;
  decided_at: string | null;
}

interface OpportunityDetailProps {
  opportunity: {
    id: string;
    opportunity_number: string;
    match_score: number;
    price: number;
    status: "new" | "in_review" | "completed";
    outcome?: "won" | "lost" | "no_fit" | null;
    match_reasons: string[];
    score_breakdown?: Record<string, any> | null;
    question_used: boolean;
    question_text?: string | null;
    question_answer?: string | null;
    expires_at?: string | null;
    declined_at?: string | null;
    created_at: string;
    contact_reveal?: {
      contact_snapshot: Record<string, any>;
      revealed_at: string;
    } | null;
    claim?: OpportunityClaim | null;
    request?: {
      category: string;
      urgency: string;
      context?: string | null;
      decision_authority: string;
      location?: string | null;
      remote_allowed: boolean;
      budget_range?: string | null;
      requester_company?: string | null;
      requester_email?: string;
      contact_phone?: string | null;
      compliance_relevant?: boolean;
      onsite_required?: boolean;
      contact_data?: Record<string, any> | null;
    };
  };
  onAccept: () => void;
  onDecline: () => void;
  onComplete: (outcome: "won" | "lost" | "no_fit") => void;
  onClaim: (reasonCode: string, reasonText: string) => Promise<void>;
  isAccepting?: boolean;
  isDeclining?: boolean;
  isClaiming?: boolean;
}

const OpportunityDetail = ({
  opportunity,
  onAccept,
  onDecline,
  onComplete,
  onClaim,
  isAccepting,
  isDeclining,
  isClaiming,
}: OpportunityDetailProps) => {
  const { t } = useTranslation();
  const request = opportunity.request;
  const [showCompletion, setShowCompletion] = useState(false);
  const [showClaim, setShowClaim] = useState(false);

  const isDeclined = !!opportunity.declined_at;
  const hideContactData = opportunity.status === "new" || isDeclined;

  const isExpired = opportunity.expires_at && new Date(opportunity.expires_at) < new Date();
  const expiresIn = opportunity.expires_at
    ? Math.max(0, Math.floor((new Date(opportunity.expires_at).getTime() - Date.now()) / (1000 * 60 * 60)))
    : null;

  const categoryLabels: Record<string, string> = {
    managed_service: "Managed Services",
    project: t("op.catProject"),
    security: t("op.catSecurity"),
    cloud: t("op.catCloud"),
    support: t("op.catSupport"),
  };

  const urgencyLabels: Record<string, string> = {
    immediate: t("op.urgImmediate"),
    within_48h: t("op.urgWithin48h"),
    within_7_days: t("op.urgWithin7d"),
    planned: t("op.urgPlanned"),
  };

  const authorityLabels: Record<string, string> = {
    self: t("op.decSelf"),
    team: t("op.decTeam"),
    recommend: t("op.decRecommend"),
  };

  return (
    <div
      className="p-8 border border-border bg-card opacity-0 animate-fade-in"
      key={opportunity.id}
    >
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <span className="text-micro">{opportunity.opportunity_number} · {new Date(opportunity.created_at).toLocaleDateString("de-DE")}</span>
            <h2 className="text-headline mt-2">
              {categoryLabels[request?.category || ""] || request?.category}
            </h2>
          </div>
          <StatusPill status={opportunity.status} />
        </div>

        {/* Expiry warning */}
        {opportunity.status === "new" && expiresIn !== null && !isExpired && (
          <div className="p-3 bg-accent/5 border border-accent/20">
            <p className="text-micro text-accent">
              {t("opportunityDetail.expiresIn", { hours: expiresIn })}
            </p>
          </div>
        )}

        {isExpired && opportunity.status === "new" && (
          <div className="p-3 bg-destructive/5 border border-destructive/20">
            <p className="text-micro text-destructive">{t("opportunityDetail.expired")}</p>
          </div>
        )}

        <div className="divider-line" />

        {/* Facts Grid */}
        <div className="grid grid-cols-2 gap-6">
          <div>
            <span className="text-micro">{t("opportunityDetail.company")}</span>
            <p className="text-body mt-1">
              {hideContactData
                ? <span className="text-muted-foreground italic">{t("opportunityDetail.confidentialUntilAccept")}</span>
                : (request?.requester_company || t("opportunityDetail.notSpecified"))}
            </p>
          </div>
          <div>
            <span className="text-micro">{t("opportunityDetail.urgency")}</span>
            <p className="text-body mt-1">
              {urgencyLabels[request?.urgency || ""] || request?.urgency}
            </p>
          </div>
          <div>
            <span className="text-micro">{t("opportunityDetail.decision")}</span>
            <p className="text-body mt-1">
              {authorityLabels[request?.decision_authority || ""] || request?.decision_authority}
            </p>
          </div>
          <div>
            <span className="text-micro">{t("opportunityDetail.matchScore")}</span>
            <p className="text-body mt-1">{opportunity.match_score}%</p>
          </div>
          {request?.location && (
            <div>
              <span className="text-micro">{t("opportunityDetail.location")}</span>
              <p className="text-body mt-1">{request.location}</p>
            </div>
          )}
          <div>
            <span className="text-micro">{t("opportunityDetail.remote")}</span>
            <p className="text-body mt-1">
              {request?.remote_allowed ? t("opportunityDetail.remotePossible") : t("opportunityDetail.onsite")}
            </p>
          </div>
          {request?.compliance_relevant && (
            <div>
              <span className="text-micro">{t("opportunityDetail.compliance")}</span>
              <p className="text-body mt-1 text-accent">{t("opportunityDetail.complianceRelevant")}</p>
            </div>
          )}
          {request?.onsite_required && (
            <div>
              <span className="text-micro">{t("opportunityDetail.onsiteLabel")}</span>
              <p className="text-body mt-1 text-accent">{t("opportunityDetail.onsiteRequired")}</p>
            </div>
          )}
        </div>

        {/* Context */}
        {request?.context && (
          <>
            <div className="divider-line" />
            <div>
              <span className="text-micro block mb-2">{t("opportunityDetail.context")}</span>
              <p className="text-caption leading-relaxed">
                {hideContactData
                  ? request.context
                      .replace(/Telefon:\s*[^\n]+/gi, "Telefon: ***")
                      .replace(/E-Mail:\s*[^\n]+/gi, "E-Mail: ***")
                      .replace(/[\w.-]+@[\w.-]+\.\w+/g, "***@***.de")
                      .replace(/(\+49|0)\s*[\d\s/()-]{6,}/g, "*** *** ***")
                  : request.context}
              </p>
            </div>
          </>
        )}

        {/* Score Breakdown */}
        {opportunity.score_breakdown && (
          <ScoreBreakdown breakdown={opportunity.score_breakdown} />
        )}

        {/* Match Reasons */}
        {opportunity.match_reasons.length > 0 && (
          <>
            <div className="divider-line" />
            <div>
              <span className="text-micro mb-4 block">{t("opportunityDetail.whySelected")}</span>
              <ul className="space-y-2">
                {opportunity.match_reasons.map((reason, i) => (
                  <li key={i} className="text-caption flex items-center gap-2">
                    <span className="w-1 h-1 bg-accent rounded-full" />
                    {reason}
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}

        <div className="divider-line" />

        {/* Price Rail */}
        <div className="flex items-center justify-between">
          <div>
            <span className="text-micro">{t("opportunityDetail.inquiryAccess")}</span>
            <p className="price-display text-2xl mt-1">{formatPrice(opportunity.price)}</p>
          </div>
        </div>

        {/* Actions based on status */}
        {opportunity.status === "new" && !isExpired && (
          <div className="flex gap-4">
            <Button
              variant="commit"
              className="flex-1"
              onClick={onAccept}
              disabled={isAccepting}
            >
              {isAccepting ? t("opportunityDetail.accepting") : t("opportunityDetail.acceptInquiry")}
            </Button>
            <Button
              variant="ghost"
              onClick={onDecline}
              disabled={isDeclining}
            >
              {t("opportunityDetail.decline")}
            </Button>
          </div>
        )}

        {(opportunity.status === "in_review" || opportunity.status === "completed") && (
          <>
            {opportunity.status === "in_review" && (
              <ContactRevealCard
                contactReveal={opportunity.contact_reveal}
                requesterEmail={request?.requester_email}
                requesterCompany={request?.requester_company}
                requesterPhone={request?.contact_phone}
                contactData={request?.contact_data as any}
              />
            )}

            {opportunity.status === "in_review" && (
              <div className="p-4 bg-secondary border border-border">
                <p className="text-caption text-center">
                  {t("opportunityDetail.responseWindow")}
                </p>
              </div>
            )}

            {!opportunity.claim && (
              <div className="space-y-3">
                <span className="text-micro block">{t("opportunityDetail.claimLabel")}</span>
                <Button variant="ghost" size="sm" onClick={() => setShowClaim(true)}>
                  {t("opportunityDetail.claimBtn")}
                </Button>
                <ClaimDialog
                  open={showClaim}
                  onOpenChange={setShowClaim}
                  onSubmit={async (reasonCode, reasonText) => {
                    await onClaim(reasonCode, reasonText);
                    setShowClaim(false);
                  }}
                  isLoading={isClaiming}
                />
              </div>
            )}
          </>
        )}

        {opportunity.status === "completed" && opportunity.outcome && (
          <div className="p-4 bg-secondary border border-border text-center">
            <span className="text-micro">
              {t("opportunityDetail.completed")} · {opportunity.outcome === "won" ? t("opportunityDetail.won") : opportunity.outcome === "lost" ? t("opportunityDetail.lost") : t("opportunityDetail.noFit")}
            </span>
          </div>
        )}

        {/* Claim status display */}
        {opportunity.claim && (
          <>
            <div className="divider-line" />
            <div className="p-4 border border-border bg-secondary/50 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-micro font-semibold">{t("opportunityDetail.claimLabel")}</span>
                <span className={cn(
                  "text-xs px-2 py-1 border",
                  (opportunity.claim.status === "APPROVED" || opportunity.claim.status === "approved")
                    ? "border-green-400/30 bg-green-500/10 text-green-600"
                    : (opportunity.claim.status === "REJECTED" || opportunity.claim.status === "rejected")
                    ? "border-red-400/30 bg-red-500/10 text-red-600"
                    : (opportunity.claim.status === "UNDER_REVIEW" || opportunity.claim.status === "submitted" || opportunity.claim.status === "SUBMITTED")
                    ? "border-yellow-400/30 bg-yellow-500/10 text-yellow-600"
                    : "border-border bg-muted text-muted-foreground"
                )}>
                  {(opportunity.claim.status === "APPROVED" || opportunity.claim.status === "approved") ? t("opportunityDetail.claimApproved")
                    : (opportunity.claim.status === "REJECTED" || opportunity.claim.status === "rejected") ? t("opportunityDetail.claimRejected")
                    : (opportunity.claim.status === "UNDER_REVIEW") ? t("opportunityDetail.claimUnderReview")
                    : t("opportunityDetail.claimSubmitted")}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-micro block mb-1">{t("opportunityDetail.submittedAt")}</span>
                  <p>{new Date(opportunity.claim.created_at).toLocaleDateString("de-DE")}</p>
                </div>
                {opportunity.claim.decided_at && (
                  <div>
                    <span className="text-micro block mb-1">{t("opportunityDetail.decidedAt")}</span>
                    <p>{new Date(opportunity.claim.decided_at).toLocaleDateString("de-DE")}</p>
                  </div>
                )}
              </div>
              {opportunity.claim.reason_code && (
                <div>
                  <span className="text-micro block mb-1">{t("opportunityDetail.reason")}</span>
                  <p className="text-sm">{opportunity.claim.reason_code.replace(/_/g, " ")}</p>
                </div>
              )}
              {(opportunity.claim.status === "REJECTED" || opportunity.claim.status === "rejected") && opportunity.claim.operator_note && (
                <div className="p-3 bg-destructive/5 border border-destructive/20">
                  <span className="text-micro block mb-1 text-destructive">{t("opportunityDetail.rejectionReason")}</span>
                  <p className="text-sm">{opportunity.claim.operator_note}</p>
                </div>
              )}
              {(opportunity.claim.status === "APPROVED" || opportunity.claim.status === "approved") && opportunity.claim.operator_note && (
                <div className="p-3 bg-accent/5 border border-accent/20">
                  <span className="text-micro block mb-1 text-accent">{t("opportunityDetail.note")}</span>
                  <p className="text-sm">{opportunity.claim.operator_note}</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OpportunityDetail;
